'use client';

import { useEffect } from 'react';
import {
  REAUTH_ERROR,
  REAUTH_LOGIN_PATH,
  isReauthenticationRequired,
} from '@/lib/sixdegree/reauth';

/**
 * Sends the browser to sign-in when any call to the dashboard's own `/api`
 * routes comes back 401 `reauthentication_required` (or was redirected to the
 * sign-in page that carries that reason). Wrapping `fetch` once here covers
 * every client component; there is no shared fetch helper to put it in.
 */
export function ReauthGuard() {
  useEffect(() => {
    const original = window.fetch;
    let leaving = false;
    const leave = () => {
      if (leaving || window.location.pathname.startsWith('/login')) return;
      leaving = true;
      window.location.assign(REAUTH_LOGIN_PATH);
    };
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const response = await original(...args);
      try {
        if (response.redirected && response.url.includes(`reason=${REAUTH_ERROR}`)) {
          leave();
        } else if (response.status === 401) {
          const input = args[0];
          const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
          const sameOrigin = url.startsWith('/') || url.startsWith(window.location.origin);
          if (sameOrigin) {
            const body = await response.clone().json().catch(() => null);
            if (isReauthenticationRequired(body)) leave();
          }
        }
      } catch {
        // Detection must never break the caller's request.
      }
      return response;
    };
    return () => {
      window.fetch = original;
    };
  }, []);
  return null;
}
